import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Editor, { type OnMount } from '@monaco-editor/react';
import type * as MonacoEditor from 'monaco-editor';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Spinner,
} from '@fluentui/react-components';
import { PlayRegular, TextAlignLeftRegular } from '@fluentui/react-icons';
import { format as formatSql } from 'sql-formatter';
import { useTheme } from '../../../contexts/ThemeContext';
import type { Chart, DatasetViewColumn } from '../../../api/types';
import SchemaHelperSidebar from './SchemaHelperSidebar';
import { registerSqlCompletionProvider } from './monacoSetup';
import type { SqlEditorSchema } from './types';
import { useEditorDisplayNames } from '../EditorDisplayNamesContext';

// ── Props ──────────────────────────────────────────────────────────────────

interface SqlEditorPanelProps {
  chart: Chart;
  previewChart?: Chart | null;
  viewColumns?: DatasetViewColumn[];
  viewName?: string;
  onSqlExecute: (sql: string) => void;
  onSqlErrorClear?: () => void;
  isExecuting?: boolean;
  sqlError?: string | null;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  viewName: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '1px 6px',
    borderRadius: '4px',
  },
  headerSpacer: {
    flex: 1,
  },
  body: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    height: 0,
    overflow: 'hidden',
  },
  editorWrapper: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
    position: 'relative' as const,
  },
});

// ── Component ──────────────────────────────────────────────────────────────

const SqlEditorPanel: React.FC<SqlEditorPanelProps> = ({
  chart,
  previewChart,
  viewColumns = [],
  viewName,
  onSqlExecute,
  onSqlErrorClear,
  isExecuting = false,
  sqlError,
}) => {
  const styles = useStyles();
  const labels = useEditorDisplayNames();
  const { isDarkMode } = useTheme();
  const editorRef = useRef<MonacoEditor.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof MonacoEditor | null>(null);
  const completionDisposableRef = useRef<MonacoEditor.IDisposable | null>(null);
  const [sqlValue, setSqlValue] = useState('');
  const [editorReady, setEditorReady] = useState(false);

  // Determine display SQL from chart or preview (PREPARE is already stripped by API)
  const displaySql = previewChart?.sql || chart.sql || '';

  // Sync editor when the source SQL changes (from AI or visual builder)
  useEffect(() => {
    setSqlValue(displaySql);
  }, [displaySql]);

  // Build schema for IntelliSense
  const schema = useMemo<SqlEditorSchema>(() => ({
    viewName: viewName || '',
    fields: viewColumns,
    variables: ['{{WHERE_CLAUSE}}', '{{date_from}}', '{{date_to}}'],
  }), [viewColumns, viewName]);

  // Store editor + monaco refs on mount
  const handleEditorMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    setEditorReady(true);
    editor.focus();
  }, []);

  // Register completions after mount and re-register when schema changes
  useEffect(() => {
    if (!editorReady || !monacoRef.current) return;

    completionDisposableRef.current?.dispose();
    completionDisposableRef.current = registerSqlCompletionProvider(monacoRef.current, schema);

    return () => {
      completionDisposableRef.current?.dispose();
    };
  }, [schema, editorReady]);

  // Handle SQL text changes (no auto-run).
  // We still update React state here for the Run button guard,
  // but markers are set via onDidChangeModelContent below.
  const handleEditorChange = useCallback((value: string | undefined) => {
    const newValue = value || '';
    setSqlValue(newValue);
    // Clear server-side error when user edits SQL so they can re-run
    if (sqlError) {
      onSqlErrorClear?.();
    }
  }, [sqlError, onSqlErrorClear]);

  // Format SQL with template variable escaping
  const handleFormat = useCallback(() => {
    try {
      // Escape {{template}} variables before formatting (sql-formatter chokes on them)
      const placeholders: string[] = [];
      const safeSql = sqlValue.replace(/\{\{\w+\}\}/g, (match) => {
        const token = `__TMPL_${placeholders.length}__`;
        placeholders.push(match);
        return token;
      });

      let formatted = formatSql(safeSql, {
        language: 'postgresql',
        tabWidth: 2,
        keywordCase: 'upper',
      });

      // Restore original template variables
      placeholders.forEach((original, i) => {
        formatted = formatted.replace(`__TMPL_${i}__`, original);
      });

      setSqlValue(formatted);
    } catch {
      // If formatting fails, keep as-is
    }
  }, [sqlValue]);

  // Run button handler — clears previous errors before executing
  const handleRun = useCallback(() => {
    if (!sqlValue.trim()) return;
    // Clear any previous server error so the user can re-run
    onSqlErrorClear?.();
    onSqlExecute(sqlValue);
  }, [sqlValue, onSqlExecute, onSqlErrorClear]);

  // Insert field at cursor in Monaco
  const handleFieldClick = useCallback((fieldName: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    const quotedField = `"${fieldName}"`;
    const selection = editor.getSelection();
    if (selection) {
      editor.executeEdits('schema-sidebar', [{
        range: selection,
        text: quotedField,
      }]);
    }
    editor.focus();
  }, []);

  // ── Server-side error markers ─────────────────────────────────────────
  // Shown after the user clicks Run and the backend returns an error.
  // Uses a separate marker owner so it doesn't conflict with live validation.
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco) return;

    const model = editor.getModel();
    if (!model) return;

    const markers: MonacoEditor.editor.IMarkerData[] = [];

    if (sqlError) {
      markers.push({
        severity: monaco.MarkerSeverity.Error,
        message: sqlError,
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: model.getLineCount(),
        endColumn: model.getLineMaxColumn(model.getLineCount()),
      });
    }

    monaco.editor.setModelMarkers(model, 'sql-server-error', markers);
  }, [sqlError, editorReady]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Text className={styles.headerTitle}>SQL Query</Text>
        {viewName && (
          <Text className={styles.viewName}>{labels.get(viewName) ?? viewName}</Text>
        )}
        <div className={styles.headerSpacer} />
        <Button
          appearance="subtle"
          size="small"
          icon={<TextAlignLeftRegular />}
          onClick={handleFormat}
          disabled={!sqlValue.trim()}
          title="Format SQL"
        >
          Format
        </Button>
        <Button
          appearance="subtle"
          size="small"
          icon={isExecuting ? <Spinner size="tiny" /> : <PlayRegular />}
          onClick={handleRun}
          disabled={isExecuting || !sqlValue.trim()}
          title="Run SQL (updates chart preview)"
        >
          Run
        </Button>
      </div>

      {/* Body: Schema sidebar + Monaco Editor */}
      <div className={styles.body}>
        {viewColumns.length > 0 && (
          <SchemaHelperSidebar
            fields={viewColumns}
            onFieldClick={handleFieldClick}
          />
        )}
        <div className={styles.editorWrapper}>
          <Editor
            height="100%"
            language="sql"
            theme={isDarkMode ? 'vs-dark' : 'light'}
            value={sqlValue}
            onChange={handleEditorChange}
            onMount={handleEditorMount}
            options={{
              minimap: { enabled: false },
              fontSize: 13,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              wordWrap: 'off',
              automaticLayout: true,
              tabSize: 2,
              suggestOnTriggerCharacters: true,
              quickSuggestions: true,
              fixedOverflowWidgets: true,
              padding: { top: 8 },
              renderWhitespace: 'selection',
              scrollbar: {
                verticalScrollbarSize: 8,
                horizontalScrollbarSize: 8,
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SqlEditorPanel;